import type { ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { useSpaceOwnRules } from '@/hooks/queries/useSpaceOwnRules';
import { Button } from '@/components/ui/button';
import Link from '@/components/ui/Link';
import { AppHeaderConfig } from '@/contexts/AppHeaderContext';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useInlineConfirm } from '@/hooks/useInlineConfirm';
import { PREVIEW_ACTIONS_NOTICE, useSpaceActionsHidden, useSpacePartnerPreview } from '@/hooks/useSpacePartnerPreview';
import { useSpaceSettings } from '@/hooks/useSpaceSettings';
import { spaceSheetHref } from '@/lib/spaces/navigation';
import { settingsAccess } from '@/lib/spaces/settings';
import type { SpaceDetail } from '@/types/spaces';
import { SettlementCard } from './SettlementParts';
import { SpacePageHeader } from './SpacePageHeader';
import { SpaceInviteManagement } from './SpaceInviteManagement';
import { SpacePartnerPreviewToggle, SpacePrivate } from './SpacePartnerPreview';
import { SpaceSettingsHistory } from './SpaceSettingsHistory';
import { SpaceSettingsPeople } from './SpaceSettingsPeople';

type Settings = ReturnType<typeof useSpaceSettings>;

export function SpaceSettingsPage() {
  const { space } = useSpaceScope();
  const { userId } = useAuth();
  if (!space || !userId) return null;
  return <SettingsSession key={`${userId}:${space.id}`} space={space} userId={userId} />;
}

function SettingsSession({ space, userId }: Readonly<{ space: SpaceDetail; userId: string }>) {
  const settings = useSpaceSettings(space.id);
  const saved = space.members.map((member) => `${member.userId}:${member.pct}`).join('|');
  return <section className="space-y-4 sm:space-y-5">
    <AppHeaderConfig hidePeriodPicker />
    <SpacePageHeader title="Configurações" space={space} />
    <ReadOnlyNotice space={space} />
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
      <div className="min-w-0 space-y-4 lg:col-span-2">
        <SpaceSettingsPeople key={saved} space={space} userId={userId} settings={settings} />
        <SpaceInviteManagement page="settings" />
        <Visibility space={space} />
        <SendingRules space={space} />
      </div>
      <div className="min-w-0 space-y-4"><SpaceSettingsHistory space={space} /><CloseTrip space={space} settings={settings} /><LeaveSpace space={space} userId={userId} settings={settings} /></div>
    </div>
  </section>;
}

function ReadOnlyNotice({ space }: Readonly<{ space: SpaceDetail }>) {
  const previewing = useSpaceActionsHidden();
  if (settingsAccess(space).manage) return null;
  return <p className="rounded-xl border border-border-subtle bg-surface-sunken p-3 text-sm text-text-secondary">
    {previewing ? PREVIEW_ACTIONS_NOTICE : space.archivedAt ? 'Espaço encerrado. As configurações ficam disponíveis só para consulta.' : 'Você tem acesso somente de leitura. Um editor pode mudar pessoas, percentuais e permissões.'}</p>;
}

// Rules are private to their owner: nobody else receives them, and a partner preview masks them like every other
// payer-only value. The chips are an extra: a failed or malformed read shows none and raises no alert (`ownRuleChips`
// checks what it reads).
function SendingRules({ space }: Readonly<{ space: SpaceDetail }>) {
  const chips = useSpaceOwnRules(space.id);
  const renderChip = (label: string, index: number) => <li key={`${index}:${label}`} className="max-w-full rounded-full border border-border-subtle bg-surface-sunken px-2.5 py-1 text-xs font-medium text-text-primary"><SpacePrivate className="break-words">{label}</SpacePrivate></li>;
  return <SettlementCard title="Suas regras de envio" action={{ label: 'Ver suas regras', href: '/spaces-rules' }}>
    {chips.length
      ? <div className="space-y-2"><p className="flex items-center gap-1 text-xs text-text-secondary"><LockSimpleIcon aria-hidden weight="bold" className="size-3" />Só você vê</p><ul aria-label="Suas regras neste espaço" className="flex min-w-0 flex-wrap gap-2">{chips.map(renderChip)}</ul></div>
      : <p className="text-sm text-text-secondary">As regras que você cria pela lista de transações aparecem aqui.</p>}
  </SettlementCard>;
}

// The preview started here follows the person across the space's pages until "Sair da prévia".
function Visibility({ space }: Readonly<{ space: SpaceDetail }>) {
  return <SettlementCard title="O que os outros veem">
    <SpacePartnerPreviewToggle space={space} />
    <ul className="divide-y divide-border-subtle">
      <VisibilityRow shared title="Despesas suas que entram no espaço">Nome, categoria, valor e quem pagou. A conta de origem, nunca.</VisibilityRow>
      <VisibilityRow title="Saldos, limites de cartão e faturas" />
      <VisibilityRow title="Renda, investimentos e metas" />
      <VisibilityRow title="Lançamentos fora do espaço e conversas com o Link" />
    </ul>
  </SettlementCard>;
}

function VisibilityRow({ title, shared = false, children }: Readonly<{ title: string; shared?: boolean; children?: ReactNode }>) {
  const Icon = shared ? CheckIcon : LockSimpleIcon;
  return <li className="flex items-start justify-between gap-4 py-3">
    <div className="min-w-0"><p className="text-sm font-semibold text-text-primary">{title}</p>{children ? <p className="text-xs leading-relaxed text-text-secondary">{children}</p> : null}</div>
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium ${shared ? 'bg-brand-100 text-brand-800' : 'bg-surface-sunken text-text-secondary'}`}><Icon aria-hidden weight="bold" className="size-3" />{shared ? 'sempre' : 'nunca'}</span>
  </li>;
}

function CloseTrip({ space, settings }: Readonly<{ space: SpaceDetail; settings: Settings }>) {
  const handleClose = () => { void settings.closeTrip(); };
  if (!settingsAccess(space).closeTrip) return null;
  return <SettlementCard title="Encerrar a viagem">
    <p className="text-xs leading-relaxed text-text-secondary">Pausa as regras de todos e arquiva a viagem, que deixa de aceitar acertos. <Link href={spaceSheetHref(space.id, 'settle')} className="cursor-pointer font-semibold text-primary underline-offset-4 hover:underline">Registre o acerto</Link> antes.</p>
    <ConfirmAction label="Encerrar viagem" confirmLabel="Sim, encerrar" question="Encerrar esta viagem? Ninguém mais envia despesas nem registra acertos nela." busy={Boolean(settings.busy)} onConfirm={handleClose} />
  </SettlementCard>;
}

// Leaving is open to every role, so the read-only view of a preview does not put it away by itself.
function LeaveSpace({ space, userId, settings }: Readonly<{ space: SpaceDetail; userId: string; settings: Settings }>) {
  const navigate = useNavigate();
  const previewing = useSpacePartnerPreview().viewerId !== null;
  const handleLeave = async () => { if (await settings.removeMember(userId)) navigate('/', { replace: true }); };
  const handleConfirm = () => { void handleLeave(); };
  if (previewing) return null;
  return <SettlementCard title="Sair do espaço">
    <p className="text-xs leading-relaxed text-text-secondary">O que já entrou continua no histórico do espaço. Nada seu nem das outras pessoas é apagado.</p>
    <ConfirmAction label={`Sair de ${space.name}`} confirmLabel="Sim, sair" question="Sair deste espaço? Você deixa de ver as despesas dele. Nada de ninguém é apagado." busy={Boolean(settings.busy)} onConfirm={handleConfirm} />
  </SettlementCard>;
}

function ConfirmAction({ label, confirmLabel, question, busy, onConfirm }: Readonly<{ label: string; confirmLabel: string; question: string; busy: boolean; onConfirm: () => void }>) {
  const confirm = useInlineConfirm();
  if (!confirm.open) return <Button variant="destructive" size="sm" className="h-auto min-h-8 max-w-full py-2 text-left whitespace-normal" autoFocus={confirm.returned} disabled={busy} onClick={confirm.ask}>{label}</Button>;
  return <div role="group" aria-label={label} className="space-y-3 rounded-xl border border-error-border bg-error-surface p-3">
    <p className="text-xs text-error-text">{question}</p>
    <div className="flex flex-wrap gap-2"><Button variant="destructive" size="sm" disabled={busy} onClick={onConfirm}>{confirmLabel}</Button><Button variant="ghost" size="sm" autoFocus disabled={busy} onClick={confirm.cancel}>Cancelar</Button></div>
  </div>;
}
